/* ==========================================================================
   After-sales storefront client. Vanilla JS, progressive enhancement, same
   conventions as theme.js — one IIFE per concern, delegated listeners,
   data-* attribute contracts, no build step. Talks to the after-sales API
   ONLY over fetch(); never touches Google credentials or privileged logic —
   those live exclusively in the backend, per docs/ARCHITECTURE.md §3.1.
   ========================================================================== */

window.efAs = window.efAs || {};

(function () {
  'use strict';

  function apiBase() {
    return (window.efAsSettings && window.efAsSettings.apiBaseUrl) || '';
  }

  function newIdempotencyKey() {
    return (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : String(Date.now()) + Math.random();
  }

  /**
   * Every mutating call gets a stable idempotency key per logical attempt —
   * the caller generates it ONCE per user action (e.g. once per "Submit"
   * click) and reuses it across retries of that SAME action, so a retried
   * network failure cannot double-submit. GET requests never send one.
   */
  async function apiFetch(path, options) {
    options = options || {};
    var headers = Object.assign({}, options.headers);
    if (options.method && options.method !== 'GET') {
      // Only default to JSON when there's actually a body to parse as JSON —
      // sending this header on a bodyless POST/DELETE (upload-complete,
      // cancel-upload, logout) makes the backend try to JSON-parse an empty
      // body and 500.
      if (options.body !== undefined) headers['Content-Type'] = headers['Content-Type'] || 'application/json';
      if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
    }

    var res;
    try {
      res = await fetch(apiBase() + path, {
        method: options.method || 'GET',
        headers: headers,
        credentials: 'include',
        body: options.body,
      });
    } catch (networkErr) {
      throw { code: 'network_error', message: 'Could not reach the server. Check your connection and try again.' };
    }

    var envelope = null;
    try { envelope = await res.json(); } catch (parseErr) { /* non-JSON response, e.g. 204 */ }

    if (!res.ok) {
      var err = (envelope && envelope.error) || { code: 'unknown_error', message: 'Something went wrong. Please try again.' };
      err.statusCode = res.status;
      throw err;
    }
    return envelope ? envelope.data : null;
  }

  window.efAs.apiFetch = apiFetch;
  window.efAs.newIdempotencyKey = newIdempotencyKey;

  /* ── Chunked upload ─────────────────────────────────────────────────────
     5MB chunks — small enough that a single failed PUT is cheap to retry,
     large enough that a 200MB video is ~40 requests, not thousands. Never
     reads the whole file into memory as one blob for hashing/Base64 (the
     spec's explicit requirement) — File.slice() streams each piece. */
  var CHUNK_SIZE = 5 * 1024 * 1024;

  async function uploadFile(file, submissionId, onProgress) {
    var session = await apiFetch('/v1/uploads/sessions', {
      method: 'POST',
      body: JSON.stringify({ submissionId: submissionId, filename: file.name, mimeType: file.type, sizeBytes: file.size }),
    });

    var offset = 0;
    while (offset < file.size) {
      var end = Math.min(offset + CHUNK_SIZE, file.size);
      var chunk = file.slice(offset, end);
      var buffer = await chunk.arrayBuffer();
      await apiFetch('/v1/uploads/sessions/' + session.uploadSessionId + '/chunks', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream', 'Content-Range': 'bytes ' + offset + '-' + (end - 1) + '/' + file.size },
        body: buffer,
      });
      offset = end;
      if (onProgress) onProgress(offset / file.size);
    }

    var result = await apiFetch('/v1/uploads/sessions/' + session.uploadSessionId + '/complete', { method: 'POST' });
    return result.attachmentId;
  }

  async function cancelUpload(uploadSessionId) {
    try { await apiFetch('/v1/uploads/sessions/' + uploadSessionId, { method: 'DELETE' }); } catch (e) { /* best-effort cleanup */ }
  }

  window.efAs.uploadFile = uploadFile;
  window.efAs.cancelUpload = cancelUpload;

  /* ── File field (dropzone + list) ────────────────────────────────────── */

  function initFileField(root) {
    var zone = root.querySelector('[data-ef-as-dropzone]');
    var input = root.querySelector('[data-ef-as-file-input]');
    var list = root.querySelector('[data-ef-as-file-list]');
    if (!zone || !input || !list) return;

    var submissionId = root.getAttribute('data-submission-id') || newIdempotencyKey();
    var attachmentIds = []; // exposed via root.efAsAttachmentIds() for the form submit handler to read
    root.efAsAttachmentIds = function () { return attachmentIds.slice(); };

    function addFiles(files) {
      Array.prototype.forEach.call(files, function (file) {
        var row = document.createElement('li');
        row.className = 'ef-as-file-row';
        row.innerHTML =
          '<span class="ef-as-file-row__name">' + escapeHtml(file.name) + '</span>' +
          '<span class="ef-as-file-row__progress"><span class="ef-as-file-row__progress-fill"></span></span>' +
          '<span class="ef-as-file-row__status">Uploading…</span>' +
          '<button type="button" class="ef-as-file-row__retry" hidden>Retry</button>' +
          '<button type="button" class="ef-as-file-row__remove" aria-label="Remove ' + escapeHtml(file.name) + '">&times;</button>';
        list.appendChild(row);

        var fill = row.querySelector('.ef-as-file-row__progress-fill');
        var status = row.querySelector('.ef-as-file-row__status');
        var retryBtn = row.querySelector('.ef-as-file-row__retry');
        var removeBtn = row.querySelector('.ef-as-file-row__remove');
        var attachmentId = null;

        function attempt() {
          row.classList.remove('is-error');
          retryBtn.hidden = true;
          status.textContent = 'Uploading…';
          fill.style.width = '0%';
          uploadFile(file, submissionId, function (pct) { fill.style.width = Math.round(pct * 100) + '%'; })
            .then(function (id) {
              attachmentId = id;
              attachmentIds.push(id);
              status.textContent = 'Done';
              fill.style.width = '100%';
              // Opt-in hook, set as a plain property on the field wrapper
              // BEFORE this file's DOMContentLoaded-triggered init runs (see
              // inbound-tracking-submit.liquid) — not a generic feature of
              // every file field, only pages that specifically want it.
              if (typeof root.efAsOnUploaded === 'function') root.efAsOnUploaded(id, file);
            })
            .catch(function (err) {
              row.classList.add('is-error');
              status.textContent = (err && err.message) || 'Upload failed';
              retryBtn.hidden = false;
            });
        }
        attempt();

        retryBtn.addEventListener('click', attempt);
        removeBtn.addEventListener('click', function () {
          if (attachmentId) attachmentIds = attachmentIds.filter(function (id) { return id !== attachmentId; });
          row.remove();
        });
      });
    }

    zone.addEventListener('click', function () { input.click(); });
    zone.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); } });
    input.addEventListener('change', function () { addFiles(input.files); input.value = ''; });
    ['dragover', 'dragenter'].forEach(function (evt) {
      zone.addEventListener(evt, function (e) { e.preventDefault(); zone.classList.add('is-dragover'); });
    });
    ['dragleave', 'drop'].forEach(function (evt) {
      zone.addEventListener(evt, function () { zone.classList.remove('is-dragover'); });
    });
    zone.addEventListener('drop', function (e) {
      e.preventDefault();
      if (e.dataTransfer && e.dataTransfer.files) addFiles(e.dataTransfer.files);
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ── Field description wiring (WCAG 3.3.1 / 4.1.2) ───────────────────────
     Every field-error message has lived in the DOM since first paint,
     hidden via CSS until .is-invalid — but was never programmatically
     associated with its control, so a screen reader user tabbing to an
     invalid field heard only the label, never the reason. Runs once per
     field group and is idempotent (safe to call again on
     shopify:section:load). */
  var errorIdCounter = 0;
  function wireFieldDescriptions(root) {
    root.querySelectorAll('.ef-as-field-group').forEach(function (group) {
      var error = group.querySelector('.ef-as-field-error');
      if (!error) return;
      if (!error.id) { error.id = 'ef-as-err-' + (++errorIdCounter); }
      group.querySelectorAll('input, select, textarea').forEach(function (control) {
        if (control.getAttribute('aria-describedby') !== error.id) {
          control.setAttribute('aria-describedby', error.id);
        }
      });
    });
  }

  /* ── Stepper ──────────────────────────────────────────────────────────── */

  function initStepper(form) {
    var steps = Array.prototype.slice.call(form.querySelectorAll('[data-ef-as-step]'));
    var stepperItems = Array.prototype.slice.call(form.querySelectorAll('[data-ef-as-stepper-item]'));
    var stepCount = form.querySelector('[data-ef-as-step-count]');
    var current = 0;

    function show(index, moveFocus) {
      steps.forEach(function (s, i) { s.hidden = i !== index; });
      stepperItems.forEach(function (s, i) {
        s.classList.toggle('is-active', i === index);
        s.classList.toggle('is-done', i < index);
        if (i === index) { s.setAttribute('aria-current', 'step'); } else { s.removeAttribute('aria-current'); }
      });
      if (stepCount) stepCount.textContent = 'Step ' + (index + 1) + ' of ' + steps.length;
      current = index;

      // Move focus into the new step so keyboard/screen-reader users get
      // some signal a step change happened at all (previously a no-op:
      // none of these step panels actually contain an h2/h3, so this always
      // queried to null). When there's no heading to focus, fall back to
      // labelling the panel itself from its matching stepper-item text
      // (assets/after-sales-progress.liquid renders one item per step, in
      // the same order as these panels) so the announced name is still
      // meaningful — e.g. "Product" — rather than an unlabeled container.
      //
      // Skipped on the very first render (moveFocus === false, from the
      // initial setup call below): a fresh page load already starts the
      // visitor at the top of the document, so there's no step CHANGE to
      // announce yet — moving focus there anyway just threw a visible focus
      // ring around the first step for every visitor, sighted or not,
      // before they had touched anything. Next/Back clicks are real step
      // changes and still move focus exactly as before.
      if (moveFocus === false) return;
      var panel = steps[index];
      var heading = panel.querySelector('h2, h3');
      var focusTarget = heading;
      if (!heading) {
        var stepperItem = stepperItems[index];
        var label = stepperItem ? stepperItem.textContent.replace(/^\d+\.\s*/, '').trim() : null;
        if (label) panel.setAttribute('aria-label', label);
        panel.setAttribute('role', 'group');
        focusTarget = panel;
      }
      focusTarget.setAttribute('tabindex', '-1');
      focusTarget.focus();
    }

    function validateStep(index) {
      var fields = steps[index].querySelectorAll('[required]');
      var firstInvalid = null;
      var valid = true;
      fields.forEach(function (field) {
        var group = field.closest('.ef-as-field-group') || field.closest('fieldset');
        var ok = field.checkValidity ? field.checkValidity() : true;
        if (group) group.classList.toggle('is-invalid', !ok);
        field.setAttribute('aria-invalid', ok ? 'false' : 'true');
        if (!ok) { valid = false; if (!firstInvalid) firstInvalid = field; }
      });

      // Required FILE fields have no native input to carry [required] — a
      // file input's "required" only means "a value was chosen", which
      // doesn't survive this component's own upload-then-clear-the-input
      // flow. Checked separately against the attachment ids the field has
      // actually collected.
      var requiredFileFields = steps[index].querySelectorAll('[data-ef-as-file-field][data-required]');
      requiredFileFields.forEach(function (group) {
        var hasFile = group.efAsAttachmentIds && group.efAsAttachmentIds().length > 0;
        group.classList.toggle('is-invalid', !hasFile);
        var fileInput = group.querySelector('input[type="file"]');
        if (fileInput) fileInput.setAttribute('aria-invalid', hasFile ? 'false' : 'true');
        if (!hasFile) { valid = false; if (!firstInvalid) firstInvalid = group.querySelector('[data-ef-as-dropzone]'); }
      });

      if (firstInvalid) firstInvalid.focus();
      return valid;
    }

    form.querySelectorAll('[data-ef-as-next]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!validateStep(current)) return;
        if (current < steps.length - 1) show(current + 1);
      });
    });
    form.querySelectorAll('[data-ef-as-back]').forEach(function (btn) {
      btn.addEventListener('click', function () { if (current > 0) show(current - 1); });
    });

    if (steps.length) show(0, false);
    return { validateStep: validateStep, currentIndex: function () { return current; }, stepCount: steps.length };
  }

  window.efAs.initFileField = initFileField;
  window.efAs.initStepper = initStepper;

  /* ── Non-sensitive draft autosave ────────────────────────────────────────
     Allowlisted fields only — never a serial number, attachment id, or
     anything that reads as sensitive, per the spec's own qualifier
     ("non-sensitive draft fields"). */
  function initDraftAutosave(form, storageKey, fieldNames) {
    try {
      var saved = JSON.parse(localStorage.getItem(storageKey) || '{}');
      fieldNames.forEach(function (name) {
        var field = form.querySelector('[name="' + name + '"]');
        if (field && saved[name] !== undefined) field.value = saved[name];
      });
    } catch (e) { /* corrupted draft, ignore */ }

    var save = debounce(function () {
      var data = {};
      fieldNames.forEach(function (name) {
        var field = form.querySelector('[name="' + name + '"]');
        if (field) data[name] = field.value;
      });
      try { localStorage.setItem(storageKey, JSON.stringify(data)); } catch (e) { /* storage full/disabled */ }
    }, 500);

    form.addEventListener('input', save);
  }

  function clearDraft(storageKey) {
    try { localStorage.removeItem(storageKey); } catch (e) { /* ignore */ }
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      var args = arguments;
      t = setTimeout(function () { fn.apply(null, args); }, ms);
    };
  }

  window.efAs.initDraftAutosave = initDraftAutosave;
  window.efAs.clearDraft = clearDraft;

  /* ── Error summary ────────────────────────────────────────────────────── */

  function showErrorSummary(root, messages) {
    var summary = root.querySelector('[data-ef-as-error-summary]');
    if (!summary) return;
    if (!messages.length) { summary.classList.remove('is-visible'); summary.innerHTML = ''; return; }
    summary.innerHTML = '<h2>Please fix the following</h2><ul>' +
      messages.map(function (m) { return '<li>' + escapeHtml(m) + '</li>'; }).join('') + '</ul>';
    summary.classList.add('is-visible');
    summary.setAttribute('tabindex', '-1');
    summary.focus();
  }

  window.efAs.showErrorSummary = showErrorSummary;

  /* ── Generic section init hook, same pattern as theme.js ─────────────── */
  function init(root) {
    root = root || document;
    wireFieldDescriptions(root);
    root.querySelectorAll('[data-ef-as-file-field]').forEach(initFileField);
  }

  document.addEventListener('DOMContentLoaded', function () { init(document); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
